import { computed } from 'vue';

import { useField, useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';

import {
    showErrorNotificationFunction,
    showSuccessNotificationFunction,
} from '@/common/helpers';
import { projectModule } from '@/features/project/store';
import yup from '@/plugins/yup';

import { TaskFieldDataType, UserFieldTypeEnum } from '../constants';
import { ICreateTaskFieldBody, TaskActions } from '../interfaces';
import { projectPlanningService } from '../services/planning.service';
import { projectPlanningModule } from '../store';

const taskFieldFormSchema = yup.object({
    name: yup.string().trim().required().label('fieldName'),
    dataType: yup
        .string()
        .nullable()
        .oneOf([...Object.values(TaskFieldDataType)])
        .required(),
    type: yup
        .string()
        .nullable()
        .oneOf([...Object.values(UserFieldTypeEnum)])
        .required(),
});
export function setupTaskFieldForm() {
    const { t } = useI18n();
    const initValues = {
        name: '',
        dataType: null,
        type: null,
    };
    const {
        handleSubmit,
        errors,
        resetForm,
        validate,
        setValues,
        setFieldValue,
        setFieldError,
        setErrors,
    } = useForm({
        initialValues: initValues,
        validationSchema: taskFieldFormSchema,
    });

    const onSubmit = handleSubmit(async (values) => {
        const updating = computed(
            () => projectPlanningModule.taskFieldsParams.action === TaskActions.UPDATE,
        );
        const selectedTaskFieldId = computed(
            () => projectPlanningModule.taskFieldsParams.selectedField?._id,
        );

        let response;
        if (updating.value) {
            response = await projectPlanningService.updateAdditionalTaskField(
                selectedTaskFieldId.value as string,
                {
                    type: values.type || '',
                    name: values.name as string,
                    projectId: projectModule.selectedProjectId || '',
                },
            );
        } else {
            response = await projectPlanningService.createAdditionalTaskField({
                name: values.name,
                dataType: values.dataType,
                type: values.type,
                projectId: projectModule.selectedProjectId || '',
                planningId: projectPlanningModule.planning?._id,
            } as ICreateTaskFieldBody);
        }
        if (response.success) {
            if (updating.value) {
                showSuccessNotificationFunction(
                    t('planning.taskFields.successMessage.update.name') as string,
                );
            } else {
                showSuccessNotificationFunction(
                    t('planning.taskFields.successMessage.create.field') as string,
                );
            }

            return response;
        } else {
            showErrorNotificationFunction(response.message as string);
        }
    });
    const { value: name } = useField('name');
    const { value: dataType } = useField('dataType');
    const { value: value } = useField('value');
    const { value: type } = useField('type');
    return {
        initValues,
        errors,
        name,
        value,
        dataType,
        setErrors,
        setFieldError,
        setFieldValue,
        validate,
        setValues,
        onSubmit,
        resetForm,
        type,
    };
}
